/**
 * The palette: what the next note will be, and what goes on the notes already written.
 *
 * The top half is note entry and shows its state -- the lit button is the value the next
 * keystroke writes -- because a palette that only sent commands would leave the hand unable
 * to see what it is about to type. The rest acts on the selection and lives in
 * `ScorePaletteMarks`, which is where the panel grows as the editor learns new marks.
 *
 * Every button sends the same thing a keystroke or a menu item would. There is one path into
 * the document and the mouse is not on a second one.
 */

import { GRACE_PATH, Glyph, NoteGlyph, REST_PATH, TIE_PATH } from "./ScoreGlyphs";
import { Button, Section } from "./ScorePaletteBits";
import ScorePaletteDrums from "./ScorePaletteDrums";
import ScorePaletteMarks from "./ScorePaletteMarks";
import ScorePaletteTab from "./ScorePaletteTab";
import { DURATIONS } from "../lib/score-menus-music";
import type { ScoreActions } from "../lib/score-actions";

export interface ScorePaletteProps {
  readonly actions: ScoreActions;
}

export default function ScorePalette({ actions }: ScorePaletteProps): JSX.Element {
  const { entry } = actions;

  return (
    <aside className="space-y-4 rounded-xl border border-rule bg-white p-3">
      <Section title="Note">
        {DURATIONS.map((duration) => (
          <Button
            key={duration.type}
            title={`${duration.label} (${duration.key})`}
            on={entry.duration.type === duration.type}
            onClick={() => actions.type({ kind: "duration", type: duration.type })}
          >
            <NoteGlyph flags={duration.flags} filled={duration.filled} />
          </Button>
        ))}
        <Button
          title="Dotted (.)"
          on={entry.duration.dots > 0}
          onClick={() => actions.type({ kind: "dots", dots: entry.duration.dots > 0 ? 0 : 1 })}
        >
          <span className="text-[18px] leading-none">·</span>
        </Button>
        <Button
          title="Triplet (/)"
          on={entry.duration.tuplet !== null}
          onClick={() =>
            actions.type({
              kind: "tuplet",
              ratio: entry.duration.tuplet === null ? { actual: 3, normal: 2 } : null,
            })
          }
        >
          <span className="font-mono text-[12px]">3</span>
        </Button>
        <Button title="Rest (R)" onClick={() => actions.type({ kind: "rest" })}>
          <Glyph d={REST_PATH} />
        </Button>
        <Button
          title="Tie to the next note (T)"
          on={entry.tie}
          onClick={() => actions.type({ kind: "tie" })}
        >
          <Glyph d={TIE_PATH} />
        </Button>
        <Button
          title="Write grace notes (S), on the beat with shift"
          on={entry.grace !== null}
          onClick={() =>
            actions.type({ kind: "grace", grace: entry.grace === null ? "acciaccatura" : null })
          }
        >
          <Glyph d={GRACE_PATH} />
        </Button>
      </Section>

      <Section title="Pitch">
        <Button
          title="Raise a semitone (↑)"
          onClick={() => actions.type({ kind: "pitch", by: "semitone", count: 1 })}
        >
          ♯
        </Button>
        <Button
          title="Lower a semitone (↓)"
          onClick={() => actions.type({ kind: "pitch", by: "semitone", count: -1 })}
        >
          ♭
        </Button>
        <Button
          title="Up a letter (alt+↑)"
          onClick={() => actions.type({ kind: "pitch", by: "step", count: 1 })}
        >
          <span className="font-mono text-[11px]">+1</span>
        </Button>
        <Button
          title="Down a letter (alt+↓)"
          onClick={() => actions.type({ kind: "pitch", by: "step", count: -1 })}
        >
          <span className="font-mono text-[11px]">−1</span>
        </Button>
        <Button
          title="Up an octave (shift+↑)"
          onClick={() => actions.type({ kind: "pitch", by: "step", count: 7 })}
        >
          <span className="font-mono text-[11px]">+8</span>
        </Button>
        <Button
          title="Down an octave (shift+↓)"
          onClick={() => actions.type({ kind: "pitch", by: "step", count: -7 })}
        >
          <span className="font-mono text-[11px]">−8</span>
        </Button>
      </Section>

      <ScorePaletteDrums actions={actions} />

      <ScorePaletteTab actions={actions} />

      <ScorePaletteMarks actions={actions} />
    </aside>
  );
}
